import { clsx } from "clsx";
import { type CSSProperties, type FC, useCallback, useContext, useEffect } from "react";
import { useFocusRing } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { type Location, useLocation, useNavigate } from "react-router";
import { ActionButton, ActionButtonIntent, HStack, Logos, TriggerButton, TriggerButtonGroup } from "widgets";
import { usePrevious } from "widgets/hooks";

import { FullscreenContext } from "@/app_context.ts";
import { BrowserSupportLevel, browserSupportLevel } from "@/app-utils/browser-compatibility.tsx";
import { type Configuration } from "@/graphql.ts";
import { FilePickerControlledDialog } from "@/modules/files/components/FilePickerControlledDialog.js";
import { isLicensed, loadInfo } from "@/redux/modules/config.ts";
import { type INavbarState, showNavbarElement } from "@/redux/modules/navbar.ts";
import { closeNFPlaybookForExecution } from "@/redux/modules/nf-playbook-execution.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { GETTING_STARTED_ELEMENT_ID, LICENSE_MODULE } from "@/settings/settings.ts";

import BlastWizard from "../../playbooks/blast/BlastWizard.js";
import { NfPlaybookWizard } from "../../playbooks/ngs/NfPlaybookWizard.js";
import CombinedSampleRegistrationNgsWizard from "../../playbooks/sample/CombinedSampleRegistrationNgsWizard.js";
import CreateProjectDialog from "../../projects/CreateProjectDialog.js";
import { ModalControlledOverlay, PopOverActionWidget } from "../overlays/index.js";
import { SmartLink } from "../SmartLink.js";
import { AdvancedSearchDialog } from "./AdvancedSearch.js";
import { AnalyzeMenu } from "./AnalyzeMenu.js";
import AuditReportForm from "./AuditReportForm.js";
import { ContentMenu } from "./ContentMenu.js";
import { GlobalSearch } from "./GlobalSearch.js";
import { HelpMenu, UserMenu } from "./NavBarMenus.js";
import { NotificationDropDown } from "./NotificationDropdown.js";
import { ProjectsMenu } from "./ProjectsMenu.js";
import style from "./styles/NavBar.module.css";

const NavBar: FC<{ style?: CSSProperties }> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navBar = useSelector<IAppState, INavbarState>((state) => state.navBar, shallowEqual);
  const selectedNfPlaybook = useSelector<IAppState, number>(
    (state) => state.nfPlaybookExecution.selectedPlaybookId,
    shallowEqual,
  );
  const isAuthenticated = useSelector<IAppState, boolean>((state) => state.auth.isAuthenticated, shallowEqual);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);
  const isUnsupportedBrowser = browserSupportLevel === BrowserSupportLevel.UNSUPPORTED;
  const [isFullscreen] = useContext(FullscreenContext);

  const onWindowResize = useCallback(() => {
    dispatch(
      showNavbarElement({
        showProjectsMenu: false,
        showVisualizePanel: false,
        showPlaybooksPanel: false,
        showAdvancedSearchPanel: false,
        showNotificationPanel: false,
      }),
    );
  }, [dispatch]);

  const toggleAdvancedSearch = useCallback(
    (show?: boolean) => {
      dispatch(
        showNavbarElement({
          showAdvancedSearchPanel: typeof show === "boolean" ? show : !navBar.showAdvancedSearchPanel,
        }),
      );
    },
    [dispatch, navBar.showAdvancedSearchPanel],
  );

  useEffect(() => {
    void dispatch(loadInfo());
    window.addEventListener("resize", onWindowResize);

    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  if (!isAuthenticated) return null;

  return (
    <>
      {!isUnsupportedBrowser ? (
        <>
          <div className={style.mainnav} style={{ display: isFullscreen ? "none" : "", ...props.style }}>
            <HStack style={{ flex: 1 }}>
              <LogoLink />
              <HStack spacing={1}>
                <ContentMenu />
                <ProjectsMenu />
                {(isLicensed(configuration, LICENSE_MODULE.EXPLORER) ||
                  isLicensed(configuration, LICENSE_MODULE.ANALYST) ||
                  isLicensed(configuration, LICENSE_MODULE.PROCESSOR)) && <AnalyzeMenu />}

                {(isLicensed(configuration, LICENSE_MODULE.EXPLORER) ||
                  isLicensed(configuration, LICENSE_MODULE.TRACKER) ||
                  isLicensed(configuration, LICENSE_MODULE.PROCESSOR)) && <PlaybookLink />}
              </HStack>
            </HStack>
            <HStack spacing={10} style={{ marginLeft: "auto" }}>
              {(isLicensed(configuration, LICENSE_MODULE.EXPLORER) ||
                isLicensed(configuration, LICENSE_MODULE.TRACKER)) && (
                <div id={GETTING_STARTED_ELEMENT_ID.GLOBAL_SEARCH}>
                  <HStack spacing={1}>
                    <GlobalSearch />
                    <PopOverActionWidget
                      title={"Advanced Search"}
                      isOpen={navBar.showAdvancedSearchPanel}
                      onCancel={() => toggleAdvancedSearch()}
                      actionWidget={() => (
                        <TriggerButtonGroup>
                          <TriggerButton
                            icon={"manage_search"}
                            label={"Advanced Search"}
                            onPress={() => toggleAdvancedSearch()}
                          />
                        </TriggerButtonGroup>
                      )}
                    >
                      {() => <AdvancedSearchDialog onApply={toggleAdvancedSearch} onCancel={toggleAdvancedSearch} />}
                    </PopOverActionWidget>
                  </HStack>
                </div>
              )}
              <HStack>
                <NotificationDropDown />
                <UserMenu />
                <HelpMenu />
              </HStack>
            </HStack>
          </div>
          {navBar.showCombinedRegistrationNgsWizard && (
            <CombinedSampleRegistrationNgsWizard
              onClose={() => dispatch(showNavbarElement({ showCombinedRegistrationNgsWizard: false }))}
            />
          )}
          {selectedNfPlaybook && <NfPlaybookWizard onClose={() => dispatch(closeNFPlaybookForExecution())} />}
          {navBar.showBlastPlaybookWizard && (
            <BlastWizard onClose={() => dispatch(showNavbarElement({ showBlastPlaybookWizard: false }))} />
          )}
          {navBar.showAuditReportForm && (
            <ModalControlledOverlay
              close={() => dispatch(showNavbarElement({ showAuditReportForm: false }))}
              isOpen={true}
            >
              {({ close }) => <AuditReportForm onClose={close} />}
            </ModalControlledOverlay>
          )}
          {navBar.showCreateProjectDialog && (
            <ModalControlledOverlay
              close={() => dispatch(showNavbarElement({ showCreateProjectDialog: false }))}
              isOpen={true}
            >
              {({ close }) => (
                <CreateProjectDialog
                  onApply={() => dispatch(showNavbarElement({ showCreateProjectDialog: false }))}
                  onCancel={close}
                />
              )}
            </ModalControlledOverlay>
          )}
          <FreeFlyingFilePickerDialog />
        </>
      ) : (
        <div className={style.mainnav}>
          <LogoLink />
          <HStack style={{ marginLeft: "auto" }}>
            <HelpMenu />
          </HStack>
        </div>
      )}
    </>
  );
};

const FreeFlyingFilePickerDialog: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navBar = useSelector<IAppState, INavbarState>((state) => state.navBar, shallowEqual);

  // in case the location changes (e.g. when clicking on some associations link) then we close explicitely the dialog since it is always present via the navbar component (special case)
  const location: Location = useLocation();
  const previousLocation = usePrevious<Location>(location);
  useEffect(() => {
    if (previousLocation && location !== previousLocation) {
      dispatch(showNavbarElement({ showFileBrowser: false }));
    }
  }, [previousLocation]);

  return (
    <FilePickerControlledDialog
      onValid={async () => {
        dispatch(showNavbarElement({ showFileBrowser: false }));
      }}
      isOpen={navBar.showFileBrowser}
      onClose={() => dispatch(showNavbarElement({ showFileBrowser: false }))}
    />
  );
};

const LogoLink: FC = () => {
  const isUnsupportedBrowser = browserSupportLevel === BrowserSupportLevel.UNSUPPORTED;
  const { isFocusVisible, focusProps } = useFocusRing();
  const appName = useSelector<IAppState, string>((state) => {
    const configuration = state.config.configuration;
    return (configuration && configuration.application && configuration.application.name) || "Selector";
  }, shallowEqual);

  return (
    <SmartLink
      to={isUnsupportedBrowser ? `/unsupported` : `/home`}
      className={clsx(style.button_home, { [style.button_focus]: isFocusVisible })}
      {...focusProps}
    >
      <img src={Logos.SelectorApplication} alt={`Genedata ${appName}`} height="24" />
    </SmartLink>
  );
};

const PlaybookLink: FC = () => {
  const navigate = useNavigate();

  return (
    <ActionButton
      id={GETTING_STARTED_ELEMENT_ID.PLAYBOOKS}
      onPress={() => {
        navigate("/playbooks");
      }}
      label={"Playbooks"}
      intent={ActionButtonIntent.Secondary}
    />
  );
};

export { NavBar };
